import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Star, Award, Users, Clock, Shield, Phone, Calendar, CheckCircle, MapPin, BadgeCheck } from 'lucide-react';
import AppointmentForm from '@/components/forms/AppointmentForm';

const TopDentistsFortWorth = () => {
  // Schema for Top Dentist page
  const topDentistSchema = {
    "@context": "https://schema.org",
    "@type": "Dentist",
    "@id": "https://fortworthdental.com/top-dentists-fort-worth#dentist",
    "name": "Fort Worth Dental",
    "alternateName": ["Top Dentists Fort Worth", "Best Dentist in Fort Worth"],
    "description": "Fort Worth Dental is recognized as one of the top dentists in Fort Worth, TX. Led by Dr. Mund (Board Certified Implant Dentist) and Dr. Razo, we have served Fort Worth families since 1990 with award-winning dental care.",
    "url": "https://fortworthdental.com/top-dentists-fort-worth",
    "logo": "https://fortworthdental.com/images/logo.png",
    "image": "https://fortworthdental.com/images/building.png",
    "telephone": "(817) 985-3337",
    "email": "info@fortworthdental.com",
    "foundingDate": "1990",
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "4620 Citylake Blvd W",
      "addressLocality": "Fort Worth",
      "addressRegion": "TX",
      "postalCode": "76132",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 32.6829,
      "longitude": -97.4089
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "1110",
      "bestRating": "5"
    },
    "award": [
      "Top Dentist Fort Worth Magazine 2024",
      "Top Dentist Fort Worth Magazine 2023",
      "Best of Fort Worth 2025",
      "Diplomate - American Board of Oral Implantology"
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Who are the top dentists in Fort Worth?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Fort Worth Dental, led by Dr. Arthur Mund and Dr. Olena Razo, is consistently ranked among the top dentists in Fort Worth. Dr. Mund is a Diplomate of the American Board of Oral Implantology with 30+ years of experience, and has been selected as a Top Dentist by Fort Worth Magazine 13 times."
        }
      },
      {
        "@type": "Question",
        "name": "What makes Fort Worth Dental one of the best dental practices in Fort Worth?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Fort Worth Dental has served the community since 1990, earning 1,100+ 5-star reviews. Our board-certified specialists offer comprehensive services including dental implants, Invisalign, cosmetic dentistry, and emergency care—all under one roof with state-of-the-art technology."
        }
      },
      {
        "@type": "Question",
        "name": "Does Fort Worth Dental offer free consultations?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! Fort Worth Dental offers FREE consultations for dental implants (including X-rays, a $475 value) and FREE Invisalign consultations. We also offer a $199 new patient special for cleanings, exams, and X-rays."
        }
      },
      {
        "@type": "Question",
        "name": "What services do the top dentists at Fort Worth Dental provide?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our top-rated dentists provide comprehensive dental care including dental implants, Invisalign clear aligners, LANAP laser gum treatment, cosmetic dentistry (veneers, whitening), emergency dental care, sedation dentistry, and general family dentistry."
        }
      }
    ]
  };

  const awards = [
    { title: "Top Dentist 2024", source: "Fort Worth Magazine", icon: Award },
    { title: "Best of Fort Worth", source: "2025 Winner", icon: Star },
    { title: "1,100+ Reviews", source: "5-Star Average", icon: Users },
    { title: "35+ Years", source: "Serving Fort Worth", icon: Clock }
  ];

  const whyChooseUs = [
    {
      title: "Board-Certified Implant Expert",
      description: "Dr. Mund is a Diplomate of the American Board of Oral Implantology—one of only a few hundred in the nation.",
      icon: BadgeCheck
    },
    {
      title: "35+ Years of Excellence",
      description: "Serving Fort Worth families since 1990 with consistent, quality dental care across generations.",
      icon: Award
    },
    {
      title: "All Services Under One Roof",
      description: "From routine cleanings to complex implants and Invisalign—no referrals needed for most procedures.",
      icon: Shield
    },
    {
      title: "State-of-the-Art Technology",
      description: "CEREC same-day crowns, digital X-rays, laser dentistry, and advanced sedation options.",
      icon: Star
    },
    {
      title: "Flexible Scheduling",
      description: "Early morning, evening, and same-day emergency appointments to fit your busy schedule.",
      icon: Clock
    },
    {
      title: "Insurance & Financing",
      description: "We accept most insurance plans and offer CareCredit financing with 0% interest options.",
      icon: CheckCircle
    }
  ];

  const services = [
    { name: "Dental Implants", link: "/dental-implants", description: "Permanent tooth replacement by board-certified specialist" },
    { name: "Invisalign", link: "/invisalign", description: "Clear aligners for straighter teeth without braces" },
    { name: "Cosmetic Dentistry", link: "/cosmetic-dentistry", description: "Veneers, whitening, and complete smile makeovers" },
    { name: "LANAP Laser Treatment", link: "/lanap", description: "Minimally invasive gum disease treatment" },
    { name: "Emergency Dentistry", link: "/emergency-dentist", description: "Same-day appointments for dental emergencies" },
    { name: "Sedation Dentistry", link: "/sedation", description: "Anxiety-free dental care for nervous patients" }
  ];

  const doctors = [
    {
      name: "Dr. Arthur Mund",
      credentials: "D.D.S., ABOI Diplomate",
      experience: "30+ years",
      specialty: "Dental Implants",
      achievements: [
        "Diplomate, American Board of Oral Implantology",
        "Selected Top Dentist 13 times by Fort Worth Magazine",
        "Thousands of successful implants placed",
        "International Congress of Oral Implantologists Fellow"
      ],
      image: "/images/team/dr-mund.jpg"
    },
    {
      name: "Dr. Olena Razo",
      credentials: "D.D.S.",
      experience: "20+ years",
      specialty: "Invisalign & Cosmetic Dentistry",
      achievements: [
        "Invisalign Certified Provider",
        "Texas A&M Baylor College of Dentistry Graduate",
        "Cosmetic dentistry specialist",
        "Fluent in English & Russian"
      ],
      image: "/images/team/dr-razo.jpg"
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Top Dentists in Fort Worth TX | Award-Winning Dental Care | Fort Worth Dental</title>
        <meta name="description" content="Looking for the top dentists in Fort Worth? Fort Worth Dental features Dr. Mund (Board Certified Implant Specialist) & Dr. Razo. 1,100+ 5-star reviews. 35+ years serving Fort Worth. FREE consultations available. Call (817) 985-3337." />
        <meta name="keywords" content="top dentists fort worth, best dentist fort worth, top rated dentist fort worth tx, best dentist in fort worth texas, fort worth top dentists, award winning dentist fort worth, best dental care fort worth" />
        <link rel="canonical" href="https://fortworthdental.com/top-dentists-fort-worth" />
        
        {/* Open Graph */}
        <meta property="og:url" content="https://fortworthdental.com/top-dentists-fort-worth" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Top Dentists in Fort Worth TX | Award-Winning Care Since 1990" />
        <meta property="og:description" content="Fort Worth Dental is home to the top dentists in Fort Worth. Dr. Mund is Board Certified in Implant Dentistry. 1,100+ 5-star reviews. FREE consultations. Call today!" />
        <meta property="og:site_name" content="Fort Worth Dental" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Top Dentists in Fort Worth TX | Fort Worth Dental" />
        <meta name="twitter:description" content="Award-winning dentists serving Fort Worth since 1990. Dr. Mund is Board Certified. 1,100+ 5-star reviews. Call (817) 985-3337." />
        
        {/* Geo Tags */}
        <meta name="geo.region" content="US-TX" />
        <meta name="geo.placename" content="Fort Worth" />
        <meta name="geo.position" content="32.6829;-97.4089" />
        
        {/* Schema */}
        <script type="application/ld+json">{JSON.stringify(topDentistSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-700 via-teal-800 to-teal-900 text-white py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="bg-yellow-500 text-yellow-900 px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                  <Star className="w-4 h-4 fill-current" /> Top Rated 2025
                </span>
                <span className="bg-white/20 text-white px-3 py-1 rounded-full text-sm font-semibold">
                  1,100+ 5-Star Reviews
                </span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Top Dentists in <span className="text-yellow-400">Fort Worth</span>
              </h1>
              
              <p className="text-xl lg:text-2xl mb-8 text-teal-100 leading-relaxed">
                Award-winning dental care since 1990. Dr. Mund is a Board Certified Implant Specialist—one of the most qualified dentists in Texas.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a
                  href="tel:8179853337"
                  className="bg-yellow-500 hover:bg-yellow-400 text-yellow-900 px-8 py-4 rounded-lg text-lg font-bold inline-flex items-center justify-center gap-2 transition shadow-lg"
                >
                  <Phone className="w-5 h-5" />
                  Call (817) 985-3337
                </a>
                <Link
                  to="/#book-appointment"
                  className="bg-white hover:bg-gray-100 text-teal-700 px-8 py-4 rounded-lg text-lg font-bold inline-flex items-center justify-center gap-2 transition shadow-lg"
                >
                  <Calendar className="w-5 h-5" />
                  Book Free Consultation
                </Link>
              </div>
              
              <div className="flex items-center gap-4 text-teal-200">
                <MapPin className="w-5 h-5" />
                <span>4620 Citylake Blvd W, Fort Worth, TX 76132</span>
              </div>
            </div>
            
            <div className="hidden lg:block">
              <img
                src="/images/services/banner-website-img-1.png"
                alt="Fort Worth Dental team - Top dentists in Fort Worth TX"
                className="rounded-xl shadow-2xl"
                width={600}
                height={450}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Awards Bar */}
      <section className="bg-gray-900 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {awards.map((award, index) => (
              <div key={index} className="text-center">
                <award.icon className="w-8 h-8 mx-auto mb-2 text-yellow-400" />
                <div className="font-bold text-lg">{award.title}</div>
                <div className="text-gray-400 text-sm">{award.source}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why We're Ranked Top Dentists */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
              Why Fort Worth Dental is Ranked Among the Top Dentists
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              For over 35 years, Fort Worth families have trusted us with their smiles. Here&apos;s what sets us apart from other dental practices.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition">
                <div className="bg-teal-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-teal-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet Our Top Dentists */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
              Meet Fort Worth&apos;s Top Dentists
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our award-winning dental team combines decades of experience with advanced training to deliver exceptional results.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {doctors.map((doctor, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="md:flex">
                  <div className="md:w-1/3">
                    <img
                      src={doctor.image}
                      alt={`${doctor.name} - Top dentist in Fort Worth TX`}
                      className="w-full h-64 md:h-full object-cover"
                    />
                  </div>
                  <div className="md:w-2/3 p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <BadgeCheck className="w-5 h-5 text-teal-600" />
                      <span className="text-sm text-teal-600 font-semibold">{doctor.specialty} Specialist</span>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-1">{doctor.name}</h3>
                    <p className="text-gray-600 mb-1">{doctor.credentials}</p>
                    <p className="text-sm text-gray-500 mb-4">{doctor.experience} Experience</p>
                    
                    <ul className="space-y-2">
                      {doctor.achievements.map((achievement, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>{achievement}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <Link
              to="/our-team"
              className="text-teal-600 hover:text-teal-700 font-semibold inline-flex items-center gap-2"
            >
              Meet Our Full Team →
            </Link>
          </div>
        </div>
      </section>

      {/* Services from Top Dentists */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
              Comprehensive Services from Fort Worth&apos;s Top Dentists
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From routine care to advanced procedures, our top-rated team provides everything you need for optimal oral health.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                className="bg-gray-50 rounded-xl p-6 hover:bg-teal-50 hover:shadow-lg transition group"
              >
                <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-teal-600">
                  {service.name}
                </h3>
                <p className="text-gray-600 text-sm">{service.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* New Patient Specials CTA */}
      <section className="py-16 bg-gradient-to-r from-teal-600 to-teal-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Experience Top-Rated Dental Care Today
            </h2>
            <p className="text-xl mb-8 text-teal-100">
              New patients receive special offers when you visit Fort Worth&apos;s top dentists!
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              <div className="bg-white/10 backdrop-blur rounded-xl p-6">
                <div className="text-3xl font-bold text-yellow-400 mb-2">FREE</div>
                <div className="text-lg font-semibold mb-1">Implant Consultation</div>
                <div className="text-sm text-teal-200">Includes X-rays ($475 value)</div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-6">
                <div className="text-3xl font-bold text-yellow-400 mb-2">FREE</div>
                <div className="text-lg font-semibold mb-1">Invisalign Consultation</div>
                <div className="text-sm text-teal-200">See if you&apos;re a candidate</div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-6">
                <div className="text-3xl font-bold text-yellow-400 mb-2">$199</div>
                <div className="text-lg font-semibold mb-1">Cleaning, Exam & X-rays</div>
                <div className="text-sm text-teal-200">New patient special</div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="tel:8179853337"
                className="bg-yellow-500 hover:bg-yellow-400 text-yellow-900 px-8 py-4 rounded-lg text-lg font-bold inline-flex items-center justify-center gap-2 transition"
              >
                <Phone className="w-5 h-5" />
                Call (817) 985-3337
              </a>
              <Link
                to="/specials"
                className="bg-white hover:bg-gray-100 text-teal-700 px-8 py-4 rounded-lg text-lg font-bold inline-flex items-center justify-center gap-2 transition"
              >
                View All Specials
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Patient Reviews */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
              What Our Patients Say About Fort Worth&apos;s Top Dentists
            </h2>
            <div className="flex items-center justify-center gap-2 text-yellow-500 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-current" />
              ))}
              <span className="text-gray-600 ml-2">4.9/5 from 1,100+ reviews</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex text-yellow-500 mb-3">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
              </div>
              <p className="text-gray-700 mb-4">&quot;Dr. Mund is hands down the best implant dentist in Fort Worth. His expertise and gentle approach made my implant procedure smooth and painless. Highly recommend!&quot;</p>
              <p className="font-semibold text-gray-800">- Michael R.</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex text-yellow-500 mb-3">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
              </div>
              <p className="text-gray-700 mb-4">&quot;I&apos;ve been coming to Fort Worth Dental for 20 years. The team treats you like family. They&apos;re truly the top dentists in the area—wouldn&apos;t go anywhere else.&quot;</p>
              <p className="font-semibold text-gray-800">- Sandra K.</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex text-yellow-500 mb-3">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
              </div>
              <p className="text-gray-700 mb-4">&quot;Dr. Razo transformed my smile with Invisalign. Professional, caring, and they offer great payment plans. Best dental experience I&apos;ve ever had!&quot;</p>
              <p className="font-semibold text-gray-800">- Jennifer T.</p>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <Link
              to="/testimonials"
              className="text-teal-600 hover:text-teal-700 font-semibold inline-flex items-center gap-2"
            >
              Read More Patient Reviews →
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-8 text-center">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-2">Who are the top dentists in Fort Worth?</h3>
                <p className="text-gray-600">Fort Worth Dental, led by Dr. Arthur Mund and Dr. Olena Razo, is consistently ranked among the top dentists in Fort Worth. Dr. Mund is a Diplomate of the American Board of Oral Implantology with 30+ years of experience, and has been selected as a Top Dentist by Fort Worth Magazine 13 times.</p>
              </div>
              
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-2">What makes Fort Worth Dental one of the best dental practices?</h3>
                <p className="text-gray-600">Fort Worth Dental has served the community since 1990, earning 1,100+ 5-star reviews. Our board-certified specialists offer comprehensive services including dental implants, Invisalign, cosmetic dentistry, and emergency care—all under one roof with state-of-the-art technology.</p>
              </div>
              
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-2">Does Fort Worth Dental offer free consultations?</h3>
                <p className="text-gray-600">Yes! We offer FREE consultations for dental implants (including X-rays, a $475 value) and FREE Invisalign consultations. We also offer a $199 new patient special for cleanings, exams, and X-rays.</p>
              </div>
              
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-2">What insurance plans do the top dentists at Fort Worth Dental accept?</h3>
                <p className="text-gray-600">We accept most major dental insurance plans including Delta Dental, Cigna, Aetna, MetLife, Guardian, and United Healthcare. We also offer CareCredit and LendingClub financing with 0% interest options.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Appointment Form Section */}
      <section className="py-16 bg-gray-50" id="book-appointment">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-6">
                Schedule Your Appointment with Fort Worth&apos;s Top Dentists
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Ready to experience award-winning dental care? Fill out the form or call us directly to schedule your visit.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="bg-teal-100 w-12 h-12 rounded-full flex items-center justify-center">
                    <Phone className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800">Call Us</div>
                    <a href="tel:8179853337" className="text-teal-600 hover:text-teal-700">(817) 985-3337</a>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="bg-teal-100 w-12 h-12 rounded-full flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800">Visit Us</div>
                    <p className="text-gray-600">4620 Citylake Blvd W, Fort Worth, TX 76132</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="bg-teal-100 w-12 h-12 rounded-full flex items-center justify-center">
                    <Clock className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800">Office Hours</div>
                    <p className="text-gray-600">Mon-Thu: 9am-6pm | Fri: 7am-2pm</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <AppointmentForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TopDentistsFortWorth;
